import type { FC, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';
import cn from 'classnames';
import { ROUTES } from 'router/routes';

import { imgSize200, imgSize400, imgSize800, imgSize1600 } from 'constants/image';
import { useImagesPreloader } from 'hooks/useImagesPreloader';
import { useIsMobile } from 'hooks/useMedia';
import { formatImagelink } from 'utils';
import { DateUtils } from 'utils/DateUtils';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import { formatOfferPrice } from 'utils/price';
import type { WithTestId } from 'types/app';

import type { OfferType } from '@Modules/offers/types';

import { NoSsr } from '@Atomic/organisms/NoSsr';
import { OfferSeller } from '@Atomic/organisms/offer/OfferSeller';
import { FullScreenGallery } from '@Atomic/molecules/FullscreenGallery';
import { Avatar } from '@Atomic/atoms/Avatar';
import { ClassicBadge } from '@Atomic/atoms/Badges/ClassicBadge';
import { Icons } from '@Atomic/atoms/Icons';
import { Image } from '@Atomic/atoms/Image';
import { LinkRouter } from '@Atomic/atoms/LinkRouter';
import { Text } from '@Atomic/atoms/Text';

import styles from './closedOfferCard.module.scss';

type Props = WithTestId<{
  offer: OfferType;
  className?: string;
  inlineToggle?: ReactNode;
  warning?: ReactNode;
}>;

export const ClosedOfferCard: FC<Props> = ({
  offer,
  className,
  inlineToggle,
  warning,
  testId = 'closed-offer-card',
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [galleryMounted, setGalleryMounted] = useState(false);
  const [fullScreenGalleryVisible, setFullScreenGalleryVisible] = useState(false);

  const primaryPhoto = offer.primaryPhoto ?? offer.photos[0];
  const photoSrc = primaryPhoto
    ? formatImagelink(primaryPhoto.upload.cropUrlTemplate, imgSize400)
    : '';
  const photoAlt = primaryPhoto?.alt || offer.title;

  const sellerName = offer.seller.name || t('offer.guest');
  const sellerHref = generatePath(ROUTES.PUBLIC_PROFILE, { uuid: offer.seller.uuid });
  const sellerAvatar = offer.seller.avatar
    ? formatImagelink(offer.seller.avatar.cropUrlTemplate, imgSize200)
    : '';

  const hasPhotos = offer.photos.length > 0;
  const extraPhotoCount = Math.max(0, offer.photos.length - 1);
  const registeredDate = offer.seller.registeredDate
    ? DateUtils.format(offer.seller.registeredDate, 'DD.MM.YY')
    : '';

  const images = useMemo(
    () =>
      offer.photos.map(({ upload, id, alt }) => ({
        id,
        alt: alt || offer.title,
        image: formatImagelink(upload.cropUrlTemplate, imgSize800),
        imageBig: formatImagelink(upload.cropUrlTemplate, imgSize1600),
      })),
    [offer.photos, offer.title]
  );

  const imagesSrcList = useMemo(
    () => (images.length > 0 ? [images[0].imageBig, ...images.map((i) => i.image)] : []),
    [images]
  );

  const { assetsLoaded } = useImagesPreloader(imagesSrcList);

  const handleThumbClick = () => {
    if (!hasPhotos) {
      return;
    }

    setGalleryMounted(true);
    setFullScreenGalleryVisible(true);
  };

  const handleGalleryClose = useCallback(() => {
    setFullScreenGalleryVisible(false);
  }, []);

  return (
    <div className={cn(styles.card, className)} data-testid={testId}>
      <div className={styles.offer}>
        {isMobile && warning}

        <div className={styles.info}>
          {hasPhotos ? (
            <button
              type="button"
              className={cn(styles.thumb, styles.thumbButton)}
              onClick={handleThumbClick}
              aria-label={t('offer.closed.openGallery', { title: offer.title })}
              data-testid={getTestIdByParts(testId, 'thumb')}
            >
              <Image src={photoSrc} alt={photoAlt} className={styles.thumbImage} />
              {extraPhotoCount > 0 && (
                <ClassicBadge
                  intent="neutral"
                  variant="glass"
                  size="md"
                  label={`+${extraPhotoCount}`}
                  className={styles.thumbBadge}
                  testId={getTestIdByParts(testId, 'photo-count')}
                />
              )}
            </button>
          ) : (
            <div className={styles.thumb} data-testid={getTestIdByParts(testId, 'thumb')}>
              <div className={styles.thumbFade} aria-hidden="true" />
              <Image src={photoSrc} alt={photoAlt} className={styles.thumbImage} />
            </div>
          )}

          <div className={styles.body}>
            <h2 className={styles.title} data-testid={getTestIdByParts(testId, 'title')}>
              {offer.title}
            </h2>

            <div className={styles.price} data-testid={getTestIdByParts(testId, 'price')}>
              {formatOfferPrice(offer.price, offer.priceType, t)}
            </div>

            {offer.urgentSale && (
              <div className={styles.badges}>
                <ClassicBadge
                  intent="neutral"
                  variant="outline"
                  size="md"
                  icon="flash"
                  label={t('badge.urgentSale')}
                  testId={getTestIdByParts(testId, 'urgent-sale')}
                />
              </div>
            )}

            {inlineToggle && (
              <div
                className={styles.inlineToggle}
                data-testid={getTestIdByParts(testId, 'inline-toggle')}
              >
                {inlineToggle}
              </div>
            )}

            {!isMobile && (
              <div className={styles.seller}>
                <Avatar
                  src={sellerAvatar}
                  alt={sellerName}
                  className={styles.sellerAvatar}
                  size="small"
                />
                <div className={styles.sellerText}>
                  <LinkRouter
                    to={sellerHref}
                    className={styles.sellerNameRow}
                    testId={getTestIdByParts(testId, 'seller-link')}
                  >
                    <Text className={styles.sellerName}>{sellerName}</Text>
                    <Icons.ChevronRight className={styles.sellerChevron} aria-hidden="true" />
                  </LinkRouter>
                  {registeredDate && (
                    <Text className={styles.date} testId={getTestIdByParts(testId, 'date')}>
                      {t('offer.closed.registered', { date: registeredDate })}
                    </Text>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isMobile && (
        <LinkRouter
          to={sellerHref}
          className={styles.seller}
          testId={getTestIdByParts(testId, 'seller-link')}
        >
          <div className={styles.sellerText}>
            <div className={styles.sellerNameRow}>
              <Text className={styles.sellerName}>{sellerName}</Text>
              <Icons.ChevronRight className={styles.sellerChevron} aria-hidden="true" />
            </div>
            {registeredDate && (
              <Text className={styles.date} testId={getTestIdByParts(testId, 'date')}>
                {t('offer.closed.registered', { date: registeredDate })}
              </Text>
            )}
          </div>
          <Avatar
            src={sellerAvatar}
            alt={sellerName}
            size="small"
            className={styles.sellerAvatar}
          />
        </LinkRouter>
      )}

      {hasPhotos && galleryMounted && (
        <NoSsr>
          <FullScreenGallery
            testId={getTestIdByParts(testId, 'gallery')}
            loading={!assetsLoaded}
            images={images}
            visible={fullScreenGalleryVisible}
            onClose={handleGalleryClose}
            footer={<OfferSeller variant="horizontal-reverse" seller={offer.seller} withLinks />}
          />
        </NoSsr>
      )}
    </div>
  );
};
